/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * MCQ Component
 *
 * @file McqComponent.jsx
 * @author Murhsid Ahamedh
 */

/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { Box, Typography, IconButton, MenuItem, Menu, Button } from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import StatusTagMenu from './StatusTagMenu';
import useAetMcqStyles from './Styles/McqStyles';
import Utils from '../../common/utils';
import messages from '../../common/defaultMessages';
import { FOOTER_CTA_LINKS, PERFORMANCE_STATUS } from '../../common/constants';
import MathTextComponent from './MathTextComponent';
import FooterDeatilsCard from './FooterDeatilsCard';
import Icon from '../icons/Icon';

const McqComponent = ({
  parentApp,
  question,
  questionNumber,
  options,
  selectedOptions,
  chapterTitle,
  attempts,
  isShowAttempt,
  fromStudent,
  status,
  onStatusChange,
  assignmentName,
  sections,
  intl,
  footer,
  footerDialogDetails,
  footerCTALinks,
  threeDotMenuItems,
  questionDetails = {}
}) => {
  const classes = Utils.getSyllabusStyles(
    parentApp,
    { ereader: null, aet: useAetMcqStyles },
    () => ({})
  );
  const [isFooterDialogOpen, setIsFooterDialogOpen] = useState(false);
  const [threeDotMenuAnchorEl, setThreeDotMenuAnchorEl] = useState(null);
  const isThreeDotMenuOpen = Boolean(threeDotMenuAnchorEl);

  // Track status locally so the selected tag updates immediately.
  const [currentStatus, setCurrentStatus] = React.useState(status);
  React.useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  const toKey = (v) => (v === null || v === undefined ? null : String(v).trim().toLowerCase());
  const normalized = Array.isArray(selectedOptions) ? selectedOptions : [];

  const { selectedIdSet } = React.useMemo(() => {
    const idSet = new Set(
      normalized
        .map((item) => (typeof item === 'object' ? toKey(item.id) : null))
        .filter((v) => v !== null)
    );

    return { selectedIdSet: idSet };
  }, [normalized]);

  if (!question || !Array.isArray(options)) return null;

  const isSelectedOption = (opt) => (
    selectedIdSet.has(toKey(opt.id))
  );

  const openFooterDialog = () => {
    setIsFooterDialogOpen(true);
  };

  const closeFooterDialog = () => {
    setIsFooterDialogOpen(false);
  };

  const getLetter = (index) => String.fromCharCode(65 + index);

  const renderFooter = () => {
    if (!footer) return null;

    const shouldShowFooterDialog =
    footerDialogDetails &&
    (footerDialogDetails.title || footerDialogDetails.content);

    return (
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Typography
          className={classes.footer}
        >
          {footer}

          {shouldShowFooterDialog && (
            <IconButton
              aria-label="More information"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openFooterDialog();
              }}
            >
              <Icon name="FooterInfoIcon" style={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Typography>

        {shouldShowFooterDialog && (
          <FooterDeatilsCard
            open={isFooterDialogOpen}
            onClose={closeFooterDialog}
            title={footerDialogDetails.title}
            content={footerDialogDetails.content}
            confirmText={footerDialogDetails.confirmText || 'Confirm'}
            cancelText={footerDialogDetails.cancelText || null}
            showCloseButton={footerDialogDetails.showCloseButton ?? true}
            onConfirm={footerDialogDetails.onConfirm || closeFooterDialog}
          />
        )}
      </div>
    );
  };

  /**
   * Render ask AI button
   * @param {Object} item
   * @returns {React.ReactNode}
   */
  const renderAskAiButton = (item) => {
    if (!item) return null;

    return (
      <Button
        type="button"
        classes={{
          root: classes.askAiCtaHeaderPill,
          label: classes.askAiCtaLabel
        }}
        onClick={(e) => {
          e.stopPropagation();
          item?.handleOnClick?.(question, questionDetails?.questionId);
        }}
        aria-label={item.label}
      >
        {item.iconName && (
          <Icon name={item.iconName} className={classes.askAiCtaIcon} />
        )}
        {item.label}
      </Button>
    );
  };

  /**
   * Render footer CTA item
   * @param {Object} item
   * @returns {React.ReactNode}
   */
  const renderFooterCtaItem = (item) => {
    if (!item) {
      return null;
    }

    if (item.id === FOOTER_CTA_LINKS.ASK_AI) {
      return renderAskAiButton(item);
    }

    return (
      <Button
        key={item.title}
        className={classes.reviewAssignmentLink}
        onClick={(e) => {
          e.stopPropagation();
          item?.handleOnClick?.();
        }}
      >
        {item.title}
      </Button>
    );
  };

  /**
   * Render footer CTA links
   * @returns {React.ReactNode}
   */
  const renderFooterCTALinks = () => {
    if (!footerCTALinks || !footerCTALinks.length) {
      return null;
    }

    return (
      <div className={classes.footerLeftCluster}>
        {footerCTALinks.map((item) => renderFooterCtaItem(item))}
      </div>
    );
  };

  /**
   * Handle three dot menu close
   * @returns {void}
   */
  const handleThreeDotMenuClose = () => {
    setThreeDotMenuAnchorEl(null);
  };

  /**
   * Handle menu open
   * @param {React.MouseEvent<HTMLButtonElement>} event
   * @returns {void}
   */
  const handleThreeDotMenuOpen = (event) => {
    setThreeDotMenuAnchorEl(event.currentTarget);
  };

  const renderThreeDotMenu = () => {
    if (!threeDotMenuItems || !threeDotMenuItems.length) {
      return null;
    }

    return (
      <Menu
        anchorEl={threeDotMenuAnchorEl}
        keepMounted
        open={isThreeDotMenuOpen}
        onClose={handleThreeDotMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        getContentAnchorEl={null}
        classes={{
          paper: classes.leftAccordionListMenuPaper,
          list: classes.courseMenuRoot
        }}
      >
        {threeDotMenuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              handleThreeDotMenuClose();
              item.callback(question);
            }}
            classes={{
              root: classes.menuItem
            }}
          >
            {item.iconName && <Icon name={item.iconName} className={classes.threeDotMenuIcon} />}
            <Typography>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const renderThreeDotMenuIcon = () => {
    if (!threeDotMenuItems || !threeDotMenuItems.length) {
      return null;
    }

    return (
      <Fragment>
        <IconButton
          aria-label="More information"
          size="small"
          onClick={handleThreeDotMenuOpen}
        >
          <Icon name="ThreeDotMenuIcon" style={{ fontSize: 16 }} />
        </IconButton>

        {renderThreeDotMenu()}
      </Fragment>
    );
  };

  return (
    <Box className={classes.root} component="section">
      <Box className={classes.header}>
        <div className={classes.headingWrapper}>
          {fromStudent && assignmentName ? (
            <Typography component="div" className={classes.heading}>
              {assignmentName}
            </Typography>
          ) : null}

          {fromStudent ? (
            (() => {
              const handleSelect = (id, label) => {
                setCurrentStatus(label);
                if (onStatusChange) onStatusChange(id);
              };

              return (
                <div className={classes.statusTagMenuContainer}>
                  <StatusTagMenu selectedTag={currentStatus} sections={sections} onSelect={handleSelect} />
                  {renderThreeDotMenuIcon()}
                </div>
              );
            })()
          ) : null}
        </div>
        <Box className={classes.headerRow}>
          {question ? (
            <div className={classes.questionStem}>
              {questionNumber && (
                <div className={classes.questionNumberWrapper}>
                  <Typography className={classes.questionTitle}>{questionNumber}</Typography>
                  <span className={classes.questionTitle}>:</span>
                </div>
              )}
              <div className={classes.chapterWrapper}>
                <MathTextComponent
                  text={question}
                  className={classes.chapterHeader}
                  component="h3"
                />
                <Box className={classes.questionRow}>
                  {chapterTitle ? (
                    <Typography className={classes.question} component="div">{chapterTitle}</Typography>
                  ) : <span />}
                  {(!fromStudent && isShowAttempt) ? (
                    <Typography className={classes.attemptsCount} component="div">{attempts} {intl.formatMessage(messages.attempts)}</Typography>
                  ) : null}
                </Box>
              </div>
            </div>
          ) : <span /> }
        </Box>
      </Box>
      <Box className={classes.content}>
        <Box className={classes.options} component="div">
          {options.map((opt, index) => {
            const isCorrect = !!opt.correct;
            const isSelected = isSelectedOption(opt);

            const rowClass = `${classes.optionRow} ${isCorrect ? classes.correct : ''} ${!isCorrect && isSelected ? classes.wrong : ''}`;
            const rowStyle = isCorrect
              ? { border: '1px solid #00B057', background: '#EBF9F0' }
              : (isSelected && !isCorrect)
                ? { border: '1px solid #FF362A', background: '#FFE7E5' }
                : undefined;

            return (
              <Box className={rowClass} style={rowStyle} key={opt.id || opt.value}>
                <Box component="span" className={`${classes.optionPrefix} ${isCorrect ? classes.prefixCorrect : isSelected ? classes.prefixWrong : ''}`}>
                  {getLetter(index)}
                </Box>
                <Box className={classes.optionContent}>
                  <MathTextComponent
                    text={opt.label || ''}
                    className={`${classes.optionLabel} ${isCorrect ? classes.correctLabel : isSelected ? classes.wrongLabel : ''}`}
                  />
                  {isCorrect && (
                  <Typography className={`${classes.optionSubLabel} ${classes.correctSubLabel}`}>{intl.formatMessage(messages.correctAnswer)}</Typography>
                  )}
                </Box>
                {isCorrect ? (
                  <Box component="span" className={`${classes.optionIcon} ${classes.statusIcon} ${classes.statusIconCorrect}`}>
                    <DoneIcon style={{ color: '#FFFFFF', fontSize: 12 }} />
                  </Box>
                ) : isSelected ? (
                  <Box component="span" className={`${classes.optionIcon} ${classes.statusIcon} ${classes.statusIconWrong}`}>
                    <CloseIcon style={{ color: '#FFFFFF', fontSize: 12 }} />
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
      <div className={footerCTALinks.length ? classes.footerContainer : classes.footerWithOnlyChild}>
        {renderFooterCTALinks()}
        {renderFooter()}
      </div>
    </Box>
  );
};

McqComponent.propTypes = {
  parentApp: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      correct: PropTypes.bool
    })
  ).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  questionNumber: PropTypes.string,
  chapterTitle: PropTypes.string,
  attempts: PropTypes.number,
  isShowAttempt: PropTypes.bool,
  fromStudent: PropTypes.bool,
  status: PropTypes.oneOf([PERFORMANCE_STATUS.NEEDS_REVIEW, PERFORMANCE_STATUS.OKAY, PERFORMANCE_STATUS.GREAT]),
  onStatusChange: PropTypes.func,
  assignmentName: PropTypes.string,
  intl: intlShape.isRequired,
  sections: PropTypes.array,
  footer: PropTypes.string,
  footerDialogDetails: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    showCloseButton: PropTypes.bool,
    onConfirm: PropTypes.func
  }),
  footerCTALinks: PropTypes.arrayOf(PropTypes.object),
  threeDotMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      callback: PropTypes.func,
      iconName: PropTypes.string
    })
  ),
  questionDetails: PropTypes.object
};

McqComponent.defaultProps = {
  selectedOptions: [],
  questionNumber: '',
  chapterTitle: '',
  attempts: 0,
  isShowAttempt: false,
  fromStudent: false,
  status: PERFORMANCE_STATUS.NEEDS_REVIEW,
  onStatusChange: undefined,
  assignmentName: '',
  sections: [],
  footer: '',
  footerDialogDetails: {
    title: '',
    content: '',
    confirmText: 'Confirm',
    cancelText: null,
    showCloseButton: false,
    onConfirm: () => {}
  },
  footerCTALinks: [],
  threeDotMenuItems: [],
  questionDetails: {}
};

export default injectIntl(McqComponent);

